import { DeviceConfigurationRequester, type DeviceStatus, DeviceStatusRequester, DisablerRequester, GraphQLVehicleGroupsRequester, type UserPreferences } from '@cobliteam/api-lib';
import type { DeviceFragment, LastAssociation } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';
import GraphQLDeviceRequester from '@cobliteam/api-lib/src/dashboard/device/GraphQLDeviceRequester';
import {
  type DeviceConfiguration,
  type DisablerEvent,
  MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH,
  type PageableContent,
  type UpdateDeviceConfiguration,
  type UUID,
} from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { Alert } from '@cobliteam/ui/components';
import { type QueryKey, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useUser, { QUERY_KEY as USER_QUERY_KEY } from '../useUser';
import { updateDevicesWithLocationData } from './helpers';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getGraphQLDeviceRequester = () => new GraphQLDeviceRequester();
const getGraphQLVehicleGroupsRequester = () => new GraphQLVehicleGroupsRequester();
const getDisablerRequester = () => new DisablerRequester({ host: env.herbieApiHost });
const getDeviceStatusRequester = () => new DeviceStatusRequester({ host: env.apiHost });
const getDeviceConfigurationRequester = () =>
  new DeviceConfigurationRequester({
    host: env.apiHost,
  });
const QUERY_KEY = 'DEVICES';

interface DeviceToDriverAssignmentProps {
  deviceId: string;
  driverId: string;
}

const useGetDevices = ({
  vehicleGroupIds,
  includeLastMovingStateTime = true,
  includeLastLocation = true,
  includeVehicle = true,
  includeDisablerStatus = false,
  includeObservationPeriodTrigger = true,
  options,
}: {
  vehicleGroupIds?: UUID[];
  includeLastMovingStateTime?: boolean;
  includeLastLocation?: boolean;
  includeVehicle?: boolean;
  includeDisablerStatus?: boolean;
  includeObservationPeriodTrigger?: boolean;
  options?: Partial<UseQueryOptions<DeviceFragment[]>>;
} = {}) => {
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = [QUERY_KEY];
  if (groupIds && groupIds.length > 0) queryKey.push(groupIds);
  if (includeObservationPeriodTrigger) queryKey.push('OBSERVATION_PERIOD_TRIGGER');

  return useQuery<DeviceFragment[]>({
    queryKey,
    queryFn: async () => {
      const devices = await getGraphQLDeviceRequester().getDevices({
        includeLastMovingStateTime,
        includeLastLocation,
        includeVehicle,
        includeDisablerStatus,
        includeObservationPeriodTrigger,
        groupIds: vehicleGroupIds,
      });
      return devices;
    },
    ...options,
  });
};

const useGetDisabledDevices = (options?: Partial<UseQueryOptions<DisablerEvent[]>>) => {
  return useQuery<DisablerEvent[]>({
    ...options,
    queryKey: [QUERY_KEY, 'FLEET_STATUS'],
    queryFn: () => getDisablerRequester().getFleetDevicesStatus(),
    refetchOnMount: false,
  });
};

const usePrefetchDevices = ({ enabled = false }: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const { getUser, getSession } = useUser();

  if (!enabled) return null;

  const fleetId = getSession().fleet.id;
  const preferencies: UserPreferences | undefined = queryClient.getQueryData([USER_QUERY_KEY, getUser().id]);
  const vehicleGroupIds = preferencies?.vehicleGroupIds;
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

  queryClient.prefetchQuery({
    queryKey: [QUERY_KEY, 'GROUP', fleetId],
    queryFn: () => getGraphQLVehicleGroupsRequester().getVehicleGroups(),
  });

  queryClient.prefetchQuery({
    queryKey,
    queryFn: async () => {
      const devices = await getGraphQLDeviceRequester().getDevices({
        groupIds: vehicleGroupIds,
      });
      return devices;
    },
  });

  return true;
};

const useGetQueryDataDevices = () => {
  const queryClient = useQueryClient();
  const getData = ({ queryKey }: { queryKey: QueryKey }) => {
    return queryClient.getQueryData(queryKey);
  };
  return getData;
};

const useSetQueryDataDevices = (updateEventsAmount: () => void) => {
  const queryClient = useQueryClient();

  const updateData = ({ queryKey, data }: { queryKey: QueryKey; data: DeviceStatusEvent[] }) => {
    // Update devices data when the main thread is idle using requestIdleCallback or requestAnimationFrame as fallback
    return new Promise<DeviceFragment[] | undefined>((resolve) => {
      const updateDevices = () => {
        const updated = queryClient.setQueryData<DeviceFragment[]>(queryKey, (devicesVehicle) => {
          if (!devicesVehicle) return devicesVehicle;
          return updateDevicesWithLocationData(devicesVehicle, data, updateEventsAmount);
        });
        resolve(updated);
      };

      // Safari doesn't support requestIdleCallback
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        (window as any).requestIdleCallback(updateDevices);
      } else if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
        (window as any).requestAnimationFrame(updateDevices);
      } else {
        // fallback if neither is available (e.g. server-side)
        updateDevices();
      }
    });
  };

  return updateData;
};

const useGetDevicesConfiguration = () => {
  return useQuery<PageableContent<DeviceConfiguration>>({
    queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
    queryFn: async () => {
      const devices = await getDeviceConfigurationRequester().getDevicesConfiguration(0, 1000);
      return devices;
    },
    refetchOnMount: false,
  });
};

const updateDevicesConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateDeviceConfiguration[],
    unknown,
    {
      devicesConfiguration: UpdateDeviceConfiguration[];
    }
  >({
    mutationFn: (request) => {
      return getDeviceConfigurationRequester().postDevicesConfiguration(request.devicesConfiguration);
    },
    onMutate: async (request) => {
      const previousData = queryClient.getQueriesData({
        queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
      });
      const { devicesConfiguration } = request;
      queryClient.setQueriesData(
        {
          queryKey: [`${QUERY_KEY}`, 'DEVICES', 'CONFIGURATION'],
        },
        (oldData: PageableContent<DeviceConfiguration> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.map((deviceConfiguration) => {
              const updatedConfiguration = devicesConfiguration.find((updatedConfigurationRequest) => updatedConfigurationRequest.deviceId === deviceConfiguration.deviceId);
              if (updatedConfiguration && updatedConfiguration.speedLimitThresholdInKmh !== MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH) {
                return {
                  ...deviceConfiguration,
                  overSpeedingAlarmEnabled: true,
                };
              }
              if (updatedConfiguration && updatedConfiguration.speedLimitThresholdInKmh === MAXIMUM_OVERSPEEDING_THRESHOLD_IN_KMPH) {
                return {
                  ...deviceConfiguration,
                  overSpeedingAlarmEnabled: false,
                };
              }
              return {
                ...deviceConfiguration,
              };
            }),
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      Alert.success('Configurações de áudio alteradas com sucesso');
    },
    onError: () => {
      Alert.error('Ops! Não foi possível salvar as configurações de alerta sonoro para os veículos. Tente novamente.');
    },
  });
};

const useGetDeviceStatus = () =>
  useQuery<DeviceStatus[]>({
    queryKey: [QUERY_KEY, 'STATUS'],
    queryFn: () => getDeviceStatusRequester().getDevicesStatus(),
    refetchOnMount: false,
  });

const useAssignDeviceToDriver = () => {
  return useMutation({
    mutationFn: ({ deviceId, driverId }: DeviceToDriverAssignmentProps) => getGraphQLDeviceRequester().assignDeviceToDriver(deviceId, driverId),
  });
};

const useUnassignDeviceToDriver = () => {
  return useMutation({
    mutationFn: ({ deviceId, driverId }: DeviceToDriverAssignmentProps) => getGraphQLDeviceRequester().unassignDeviceToDriver(deviceId, driverId),
  });
};

const useGetDeviceLastAssociations = (deviceId: string, options?: Partial<UseQueryOptions<LastAssociation>>) => {
  return useQuery<LastAssociation>({
    ...options,
    queryKey: [QUERY_KEY, 'LAST_ASSOCIATION', deviceId],
    queryFn: () => getGraphQLDeviceRequester().getDeviceLastAssociation(deviceId),
    refetchOnMount: false,
  });
};

export { QUERY_KEY };
export {
  useGetDevicesConfiguration,
  updateDevicesConfiguration,
  useGetDevices,
  useGetDisabledDevices,
  usePrefetchDevices,
  useGetQueryDataDevices,
  useSetQueryDataDevices,
  useGetDeviceStatus,
  useAssignDeviceToDriver,
  useUnassignDeviceToDriver,
  useGetDeviceLastAssociations,
};
